import { Link, useParams } from "react-router-dom";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import { useRpcResource } from "../lib/useRpcResource";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../org/OrganizationProvider";

type Metric = {
  key: string;
  label: string;
  value: number | string | null;
  format?: "currency" | "decimal";
};
type QueueItem = {
  id: string;
  primary: string;
  secondary?: string | null;
  status?: string | null;
  updated_at?: string | null;
};
type DepartmentWorkspace = {
  contract_version: string;
  generated_at: string;
  department: { key: string; name: string; description: string };
  capabilities: { can_view: boolean; can_manage: boolean };
  metrics: Metric[];
  work_queue: { items: QueueItem[] };
};
type Tool = {
  title: string;
  description: string;
  route: string;
  primary?: boolean;
};

const tools: Record<string, Tool[]> = {
  executive: [
    {
      title: "Decision Center",
      description:
        "Company KPIs, department health, attention items and operating decisions.",
      route: "/executive",
      primary: true,
    },
    {
      title: "Targets & Snapshots",
      description: "Manage targets, reporting periods and executive snapshots.",
      route: "/executive/setup",
    },
  ],
  operations: [
    {
      title: "Jobs & Dispatch",
      description:
        "Paid/confirmed Sales Orders become named Jobs for readiness, phase scheduling and operational status.",
      route: "/operations/jobs",
      primary: true,
    },
    {
      title: "Dispatch Calendar",
      description:
        "Weekly visual schedule with unscheduled Job phases waiting for dispatch.",
      route: "/operations/calendar",
    },
    {
      title: "Staffing Board",
      description:
        "Compare Job value with crew performance, then assign crews/people to phases and time windows.",
      route: "/operations/staffing",
    },
    {
      title: "Crew Management",
      description: "Build reusable crews and maintain crew membership.",
      route: "/operations/crews",
    },
  ],
  warehouse: [
    {
      title: "Warehouse Task Board",
      description:
        "Pick, prep, QC, stage, load and release work assigned to the warehouse.",
      route: "/warehouse/tasks",
      primary: true,
    },
  ],
  inventory: [
    {
      title: "Inventory & Assets",
      description:
        "Serialized assets, bulk positions, availability and asset history.",
      route: "/inventory/assets",
      primary: true,
    },
    {
      title: "Transfers",
      description:
        "Move inventory between operating locations through the governed transfer lifecycle.",
      route: "/inventory/transfers",
    },
    {
      title: "Adjustments",
      description:
        "Review, approve and apply controlled inventory adjustments.",
      route: "/inventory/adjustments",
    },
  ],
  logistics: [
    {
      title: "Route Board",
      description:
        "Plan, staff, dispatch and complete transportation routes and stops.",
      route: "/logistics/routes",
      primary: true,
    },
    {
      title: "Fleet & Drivers",
      description:
        "Vehicles, trailers, driver profiles, status and qualification resources.",
      route: "/logistics/fleet",
    },
  ],
  field: [
    {
      title: "Local Field Operations",
      description:
        "Open the Local crew schedule, assigned task checklist and complete installation BOM.",
      route: "/field/crew",
      primary: true,
    },
    {
      title: "Crew Timecards",
      description: "Review team time recorded against Jobs and phases.",
      route: "/time/timecards",
    },
  ],
  maintenance: [
    {
      title: "Maintenance Work Orders",
      description:
        "Corrective work, inspections, labor, parts, cost and return-to-service.",
      route: "/maintenance/work-orders",
      primary: true,
    },
    {
      title: "Preventive Maintenance",
      description: "Preventive plans, recurring tasks and due-work generation.",
      route: "/maintenance/plans",
    },
  ],
  hr: [
    {
      title: "Workforce",
      description: "Employees, status, positions, supervisors and leave.",
      route: "/hr/workforce",
      primary: true,
    },
    {
      title: "Qualifications & Training",
      description: "Skills, certifications, training and HR setup.",
      route: "/hr/setup",
    },
  ],
  timekeeping: [
    {
      title: "Timecards & Clock",
      description:
        "Clock activity, manual time, corrections, approvals and employee timecards.",
      route: "/time/timecards",
      primary: true,
    },
    {
      title: "Payroll Export",
      description:
        "Approved locked timecards and governed payroll export batches.",
      route: "/time/payroll",
    },
    {
      title: "Time & Pay Setup",
      description: "Work rules, compensation and timekeeping configuration.",
      route: "/time/setup",
    },
  ],
  finance: [
    {
      title: "Accounts Receivable",
      description:
        "Open Invoices, payment terms, due dates, aging and payment release.",
      route: "/finance/receivables",
      primary: true,
    },
    {
      title: "Financial Overview",
      description:
        "Completed-job forecast versus actual revenue, direct cost and contribution.",
      route: "/finance",
    },
    {
      title: "Cost Controls & Overhead",
      description:
        "Operating costs, product cost history and accounting controls.",
      route: "/finance/cost-controls",
    },
  ],
  service: [
    {
      title: "Service Desk",
      description:
        "Customer tickets, SLA, assignment, escalation, comments and resolution.",
      route: "/service",
      primary: true,
    },
    {
      title: "Service Setup",
      description: "Queues, categories and SLA policies.",
      route: "/service/setup",
    },
  ],
  quality: [
    {
      title: "Quality & Safety Workspace",
      description:
        "Quality incidents, damage accountability, safety observations and corrective action.",
      route: "/quality",
      primary: true,
    },
  ],
  performance: [
    {
      title: "Crew Performance Scorecards",
      description:
        "Crew-level periods, scorecards, ratings, attribution and governed adjustments.",
      route: "/performance",
      primary: true,
    },
    {
      title: "Crew Performance Setup",
      description:
        "Crew scoring profile, metrics and performance configuration.",
      route: "/performance/setup",
    },
  ],
  admin: [
    {
      title: "System Administration",
      description: "Platform status, governance and system controls.",
      route: "/admin",
      primary: true,
    },
    {
      title: "Production Onboarding",
      description: "Launch-readiness checklist and operating setup status.",
      route: "/admin/onboarding",
    },
    {
      title: "Bulk Data Import",
      description:
        "Preview-first onboarding for master data and operational resources.",
      route: "/admin/import",
    },
    {
      title: "Master Data",
      description: "Locations, departments, products and services.",
      route: "/admin/master-data",
    },
    {
      title: "Pricing & Labor Standards",
      description:
        "Price lists, rental rates, service pricing and labor assumptions.",
      route: "/admin/pricing",
    },
    {
      title: "Users & Access",
      description: "Memberships, roles, scopes and invitations.",
      route: "/admin/users",
    },
    {
      title: "Integrations",
      description:
        "Provider connections, subscriptions and sync configuration.",
      route: "/admin/integrations",
    },
    {
      title: "Automation",
      description: "Automation rules, execution and governance.",
      route: "/admin/automation",
    },
  ],
};

function formatMetric(metric: Metric) {
  if (metric.value === null || metric.value === undefined) return "—";
  const numeric =
    typeof metric.value === "number" ? metric.value : Number(metric.value);
  if (metric.format === "currency" && Number.isFinite(numeric))
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(numeric);
  if (metric.format === "decimal" && Number.isFinite(numeric))
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
      numeric,
    );
  if (typeof metric.value === "number")
    return new Intl.NumberFormat("en-US").format(metric.value);
  return String(metric.value);
}
function cleanStatus(status?: string | null) {
  return status ? status.replaceAll("_", " ") : "active";
}
export function DepartmentWorkspacePage() {
  const { departmentKey } = useParams();
  const { organizationId } = useOrganization();
  const resource = useRpcResource<DepartmentWorkspace>(async () => {
    if (!organizationId || !departmentKey)
      return {
        data: null,
        error: { message: "Department context is missing." },
      };
    const result = await supabase.rpc("get_department_workspace_v2", {
      p_organization_id: organizationId,
      p_department_key: departmentKey,
    });
    return {
      data: (result.data ?? null) as DepartmentWorkspace | null,
      error: result.error ? { message: result.error.message } : null,
    };
  }, [organizationId, departmentKey]);
  if (resource.loading) return <Loading />;
  if (resource.error)
    return (
      <>
        <PageHeader title="Department workspace" />
        <ErrorPanel message={resource.error} />
      </>
    );
  if (!resource.data)
    return <ErrorPanel message="Department workspace did not return data." />;
  const data = resource.data;
  const queue = data.work_queue?.items ?? [];
  const departmentTools = tools[departmentKey ?? ""] ?? [];
  return (
    <>
      <PageHeader
        title={data.department.name}
        subtitle={data.department.description}
        actions={
          <button onClick={() => void resource.reload()}>Refresh</button>
        }
      />
      <div className="metric-grid five">
        {data.metrics.map((metric) => (
          <div className="metric" key={metric.key}>
            <span>{metric.label}</span>
            <strong>{formatMetric(metric)}</strong>
          </div>
        ))}
      </div>
      {departmentTools.length > 0 && (
        <Card title="Workspaces">
          <div className="department-tool-grid">
            {departmentTools.map((tool) => (
              <Link
                className={`department-tool-card${tool.primary ? " primary-tool" : ""}`}
                to={tool.route}
                key={tool.route}
              >
                <div>
                  <strong>{tool.title}</strong>
                  <p>{tool.description}</p>
                </div>
                <span>Open →</span>
              </Link>
            ))}
          </div>
        </Card>
      )}
      <div className="detail-grid department-detail-grid">
        <Card title="Current work">
          {queue.length === 0 ? (
            <div className="empty-state">
              No current records in this department queue.
            </div>
          ) : (
            <div className="stack-list">
              {queue.map((item) => (
                <div className="list-row department-queue-row" key={item.id}>
                  <div>
                    <strong>{item.primary}</strong>
                    {item.secondary && <span>{item.secondary}</span>}
                  </div>
                  <span
                    className={`status-pill status-${cleanStatus(item.status).replaceAll(" ", "-")}`}
                  >
                    {cleanStatus(item.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title="Access">
          <dl className="detail-list compact-detail-list">
            <dt>Workspace</dt>
            <dd>{data.capabilities.can_view ? "Enabled" : "Restricted"}</dd>
            <dt>Manage</dt>
            <dd>{data.capabilities.can_manage ? "Enabled" : "View only"}</dd>
            <dt>Updated</dt>
            <dd>{new Date(data.generated_at).toLocaleString()}</dd>
          </dl>
        </Card>
      </div>
    </>
  );
}
